"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { AddCreditsDialog } from "@/app/(app)/manage/billing/billing.overview";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useWelcomeCreditReveal } from "@/components/welcome-credit-reveal";
import { useAuth } from "@/lib/auth";
import { useBillingOverview } from "@/lib/billing/useBillingOverview";
import { useCreditTopUp } from "@/lib/billing/useCreditTopUp";
import { orgRoleCanManageBilling } from "@/lib/org/org-constants";
import { useLocalStorageState } from "@/lib/useLocalStorageState";

export const LOW_CREDIT_PROMO_DISMISSED_KEY = "dismissed-promo-low-credit";

/**
 * One-time low-credit nudge when remaining balance is ≤20% of the org's
 * lifetime free grant (welcome and/or admin invite credits). Falls back to the
 * configured welcome amount when no free grant was recorded. Suppressed after a
 * second positive credit grant/top-up, and never shown while the welcome promo is open.
 */
export const DashboardLowCreditModal = () => {
  const { orgRole } = useAuth();
  const canManageBilling = orgRoleCanManageBilling(orgRole);
  const { configuredWelcomeAmountCents, shouldShowPromo: welcomePromoOpen } = useWelcomeCreditReveal();
  const { data: billingData, isPending } = useBillingOverview();
  const topUp = useCreditTopUp();
  const [dismissed, setDismissed] = useLocalStorageState<boolean>(LOW_CREDIT_PROMO_DISMISSED_KEY, false, {
    dataType: "boolean",
    initializeWithValue: false,
  });
  const [hydrated, setHydrated] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const overview = billingData?.billing_overview;
  const balanceMicro = Number(overview?.credit_balance_micro ?? 0);
  const positiveCreditEntryCount = overview?.positive_credit_entry_count ?? 0;
  const lifetimeFreeGrantedMicro = Number(overview?.lifetime_free_granted_micro ?? 0);
  const configuredWelcomeMicro = configuredWelcomeAmountCents * 10_000;
  // Prefer the org's actual free grant (admin invite $50/$100, welcome $25, etc.)
  // over the global welcome default so the 20% threshold matches the grant.
  const freeGrantMicro =
    Number.isFinite(lifetimeFreeGrantedMicro) && lifetimeFreeGrantedMicro > 0
      ? lifetimeFreeGrantedMicro
      : configuredWelcomeMicro;
  const atOrBelow20Pct = freeGrantMicro > 0 && Number.isFinite(balanceMicro) && balanceMicro <= freeGrantMicro * 0.2;
  const onlyInitialFreeCredit = positiveCreditEntryCount <= 1;
  const shouldShow =
    hydrated &&
    !isPending &&
    overview != null &&
    atOrBelow20Pct &&
    onlyInitialFreeCredit &&
    !dismissed &&
    !welcomePromoOpen &&
    !creditsOpen &&
    canManageBilling;

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, [setDismissed]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      dismiss();
    }
  };

  const handleAddCredits = () => {
    dismiss();
    setCreditsOpen(true);
  };

  return (
    <>
      <Dialog open={shouldShow} onOpenChange={handleOpenChange}>
        <DialogContent
          showCloseButton
          className="max-w-md gap-0 overflow-hidden border-none bg-white p-0 text-zinc-900 sm:rounded-2xl **:data-[slot=dialog-close]:text-zinc-50 **:data-[slot=dialog-close]:opacity-80"
        >
          <div className="bg-[#242220]">
            <Image
              src="/marketing/mkt-fuel-empty.svg"
              alt="Empty fuel gauge"
              width={495}
              height={152}
              className="h-auto w-full"
              priority
            />
          </div>
          <div className="flex flex-col gap-6 px-6 pt-6 pb-6 sm:px-8">
            <DialogHeader className="gap-2 text-left sm:text-left">
              <DialogTitle className="text-2xl font-semibold tracking-tight text-zinc-900">
                Keep using Radium?
              </DialogTitle>
              <DialogDescription className="text-sm text-zinc-600">
                You&apos;ve used 80% of your free credit. Add more to keep building without interruption.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end sm:space-x-0 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full border-zinc-900 bg-transparent text-zinc-900 hover:bg-zinc-100 sm:w-auto"
                onClick={dismiss}
              >
                Not now
              </Button>
              {canManageBilling ? (
                <Button
                  type="button"
                  className="w-full rounded-full bg-zinc-900 text-zinc-50 hover:bg-zinc-800 sm:w-auto"
                  onClick={handleAddCredits}
                >
                  Add more credits
                </Button>
              ) : null}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {canManageBilling && overview != null ? (
        <AddCreditsDialog
          open={creditsOpen}
          onOpenChange={setCreditsOpen}
          trigger={null}
          topUp={topUp}
          paymentMethods={overview.payment_methods}
          invoices={overview.invoices}
          monthlySpendLimitMicro={overview.monthly_spend_limit_micro}
          currentSettings={overview.auto_recharge}
        />
      ) : null}
    </>
  );
};
